import { useCallback, useReducer } from 'react';
import { TabMenuItemGeneral, TabText } from '../../components/TabMenuItem/TabMenuItem';
import { deepCopy } from '../../common/objectUtils';

type ActionType = 'set-tab' | 'set-entered';

export interface TabBarState {
  items: TabMenuItemGeneral[];
  current: TabText;
  updated?: Exclude<TabText, 'my-selections' | 'payout-table'>;
  prev?: TabText;
}

export interface Action {
  action: ActionType;
  value?: TabText;
  selectionMade?: boolean;
}

export interface TabBarDispatches {
  setTab: (props: { item: TabText; selectionMade: boolean }) => void;
  setEntered: () => void;
}

interface UpdateTabsProps {
  items: TabMenuItemGeneral[];
  current: TabText;
  prev?: TabText;
  selectionMade?: boolean;
}

const updateTabs = ({ items, current, prev, selectionMade }: UpdateTabsProps) => {
  const copy = deepCopy(items);
  const currentIndex = copy.findIndex((item) => item.tabText === current);
  copy[currentIndex].focused = true;
  if (prev) {
    const prevIndex = copy.findIndex((item) => item.tabText === prev);
    copy[prevIndex].focused = false;
    if (selectionMade) copy[prevIndex].selectionMade = true;
  }
  return copy;
};

export const generateTabs = (items: TabText[]): TabMenuItemGeneral[] => {
  return items.map((item) => {
    return { tabText: item, selectionMade: false, focused: false, type: 'general' };
  });
};

const reducer = (state: TabBarState, action: Action) => {
  switch (action.action) {
    case 'set-tab':
      if (state.current === action.value) {
        return state;
      }
      return {
        current: action?.value ?? state.current,
        items: updateTabs({
          items: state.items,
          current: action.value ?? state.current,
          prev: state.current,
          selectionMade: action?.selectionMade,
        }),
        prev: state.current,
      };

    case 'set-entered':
      const copy = deepCopy(state.items);
      copy.forEach((item) => {
        if (item.tabText !== 'my-selections') {
          item.selectionMade = true;
          item.focused = false;
        } else {
          item.focused = true;
        }
      });
      //have to do this because TS doesn't recognise that 'my-selections' is a valid tabText
      const newState: TabBarState = { items: copy, current: 'my-selections' };
      return newState;
  }
};

export const useTabBarReducer = (current: TabText, items: TabText[]): [TabBarState, TabBarDispatches] => {
  const tabs = generateTabs(items);
  const defaultState: TabBarState = { current, items: updateTabs({ items: tabs, current }) };

  const [state, dispatch] = useReducer(reducer, defaultState);

  const setTab: TabBarDispatches['setTab'] = useCallback(({ item, selectionMade = false }) => {
    dispatch({
      action: 'set-tab',
      value: item,
      selectionMade,
    });
  }, []);

  const setEntered: TabBarDispatches['setEntered'] = useCallback(() => {
    dispatch({ action: 'set-entered' });
  }, []);

  return [state, { setTab, setEntered }];
};
